<template>
    <AppDialog
        v-model="show"
        :title="$t('facturacion.dialogo-titulo')"
        :subtitle="$t('facturacion.dialogo-subtitulo')"
    >
        <!-- Content -->
        <template #default>
            <!-- Customer Type Selection -->
            <div class="mb-6">
                <div class="text-body-1 text-text text-medium-emphasis mb-3">
                    {{ $t("facturacion.cliente.etiqueta") }}
                </div>
                <v-radio-group v-model="formData.tipo" inline hide-details>
                    <v-radio
                        :label="$t('facturacion.cliente.1')"
                        :value="1"
                        color="primary"
                        class="text-body-1 text-textDarken mr-6"
                    ></v-radio>
                    <v-radio
                        :label="$t('facturacion.cliente.2')"
                        :value="2"
                        color="primary"
                        class="text-body-1 text-textDarken"
                    ></v-radio>
                </v-radio-group>
            </div>

            <!-- Show form based on tipo selection -->
            <div v-if="formData.tipo">
                <!-- Outside Mexico Form (tipo = 2) -->
                <div v-if="formData.tipo === 2">
                    <div class="text-body-1 text-text mb-3">
                        {{ $t("facturacion.cliente.instrucciones-empresa") }}
                    </div>
                    <v-text-field
                        v-model="formData.razon_social"
                        :label="$t('facturacion.empresa')"
                        variant="outlined"
                        persistent-placeholder
                        density="comfortable"
                        :error-messages="errors.razon_social"
                    ></v-text-field>
                </div>

                <!-- Mexico Form (tipo = 1) -->
                <div v-if="formData.tipo === 1">
                    <div class="text-body-1 text-text mb-3">
                        {{ $t("facturacion.cliente.instrucciones-fiscal") }}
                    </div>

                    <v-text-field
                        v-model="formData.razon_social"
                        :label="$t('facturacion.razon-social')"
                        variant="outlined"
                        persistent-placeholder
                        density="comfortable"
                        :error-messages="errors.razon_social"
                        class="mb-3"
                    ></v-text-field>

                    <v-text-field
                        v-model="formData.rfc"
                        :label="$t('facturacion.rfc')"
                        variant="outlined"
                        persistent-placeholder
                        density="comfortable"
                        :error-messages="errors.rfc"
                        class="mb-3"
                    ></v-text-field>

                    <v-text-field
                        v-model="formData.codigo_postal"
                        :label="$t('facturacion.codigo-postal')"
                        variant="outlined"
                        persistent-placeholder
                        density="comfortable"
                        :error-messages="errors.codigo_postal"
                        class="mb-3"
                    ></v-text-field>

                    <v-autocomplete
                        v-model="formData.regimen_fiscal_id"
                        :items="regimenesFiscales"
                        :item-title="(item) => item?.nombre_completo || ''"
                        item-value="id"
                        :label="$t('facturacion.regimen')"
                        variant="outlined"
                        persistent-placeholder
                        density="comfortable"
                        :error-messages="errors.regimen_fiscal_id"
                        class="mb-3"
                        clearable
                    ></v-autocomplete>

                    <v-autocomplete
                        v-model="formData.uso_cfdi_id"
                        :items="usosCfdi"
                        :item-title="(item) => item?.nombre_completo || ''"
                        item-value="id"
                        :label="$t('facturacion.uso-cfdi')"
                        variant="outlined"
                        persistent-placeholder
                        density="comfortable"
                        :error-messages="errors.uso_cfdi_id"
                        class="mb-3"
                        clearable
                    ></v-autocomplete>

                    <v-file-input
                        v-model="formData.constancia"
                        :label="$t('facturacion.constancia')"
                        variant="outlined"
                        persistent-placeholder
                        persistent-hint
                        density="comfortable"
                        prepend-icon=""
                        prepend-inner-icon="mdi-paperclip"
                        accept="application/pdf"
                        :error-messages="errors.constancia"
                        @change="validateFile"
                    ></v-file-input>
                </div>
            </div>
        </template>

        <!-- Actions -->
        <template #actions>
            <v-spacer></v-spacer>
            <v-btn
                variant="outlined"
                color="primary"
                class="text-primary"
                @click="handleCancel"
            >
                {{ $t("boton-cancelar") }}
            </v-btn>
            <v-btn
                v-if="formData.tipo"
                variant="flat"
                color="primary"
                @click="handleSave"
                :loading="loading"
            >
                {{ $t("boton-guardar") }}
            </v-btn>
            <v-spacer></v-spacer>
        </template>
    </AppDialog>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { router } from "@inertiajs/vue3";
import axios from "axios";
import { useI18n } from "vue-i18n";
import AppDialog from "./AppDialog.vue";

const { t: $t } = useI18n();

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    standId: {
        type: [Number, String],
        required: true,
    },
    forma: {
        type: Object,
        required: false,
        default: null,
    },
    orden: {
        type: Object,
        required: false,
        default: null,
    },
});

const emit = defineEmits(["update:modelValue", "saved", "cancel"]);

const show = computed({
    get: () => props.modelValue,
    set: (value) => emit("update:modelValue", value),
});

// Form data
const formData = ref({
    tipo: null,
    razon_social: "",
    rfc: "",
    codigo_postal: "",
    regimen_fiscal_id: null,
    uso_cfdi_id: null,
    constancia: null,
});

// Validation errors
const errors = ref({
    razon_social: "",
    rfc: "",
    codigo_postal: "",
    regimen_fiscal_id: "",
    uso_cfdi_id: "",
    constancia: "",
});

// Loading state
const loading = ref(false);

// Catalog data
const regimenesFiscales = ref([]);
const usosCfdi = ref([]);

// Load catalogs
const loadCatalogs = async () => {
    try {
        // Fetch both catalogs in parallel
        const [regimenesResponse, usosResponse] = await Promise.all([
            axios.get("/api/regimenes-fiscales"),
            axios.get("/api/usos-cfdi"),
        ]);

        regimenesFiscales.value = regimenesResponse.data;
        usosCfdi.value = usosResponse.data;
    } catch (error) {
        console.error("Error loading catalogs:", error);
    }
};

// Watch for dialog open
watch(show, (newValue) => {
    if (newValue) {
        loadCatalogs();
        resetForm();
    }
});

// Reset form
const resetForm = () => {
    formData.value = {
        tipo: null,
        razon_social: "",
        rfc: "",
        codigo_postal: "",
        regimen_fiscal_id: null,
        uso_cfdi_id: null,
        constancia: null,
    };
    errors.value = {
        razon_social: "",
        rfc: "",
        codigo_postal: "",
        regimen_fiscal_id: "",
        uso_cfdi_id: "",
        constancia: "",
    };
};

// Validate file
const validateFile = () => {
    errors.value.constancia = "";

    // Get the actual file - v-file-input can return array or single file
    const file = Array.isArray(formData.value.constancia)
        ? formData.value.constancia[0]
        : formData.value.constancia;

    if (file) {
        // Check file type - must be PDF
        if (file.type !== "application/pdf") {
            errors.value.constancia = $t(
                "facturacion.validaciones.archivo-pdf"
            );
            formData.value.constancia = null;
            return false;
        }

        // Check file size (2MB = 2 * 1024 * 1024 bytes)
        const maxSize = 2 * 1024 * 1024;
        if (file.size > maxSize) {
            errors.value.constancia = $t(
                "facturacion.validaciones.archivo-tamano"
            );
            formData.value.constancia = null;
            return false;
        }
    }

    return true;
};

// Validate form
const validateForm = () => {
    let isValid = true;
    errors.value = {
        razon_social: "",
        rfc: "",
        codigo_postal: "",
        regimen_fiscal_id: "",
        uso_cfdi_id: "",
        constancia: "",
    };

    // Common validation - razon_social is required for both types
    if (!formData.value.razon_social.trim()) {
        errors.value.razon_social = $t(
            "facturacion.validaciones.razon-social-requerido"
        );
        isValid = false;
    }

    // Mexico specific validation (tipo === 1)
    if (formData.value.tipo === 1) {
        // RFC is required for Mexico
        if (!formData.value.rfc.trim()) {
            errors.value.rfc = $t("facturacion.validaciones.rfc-requerido");
            isValid = false;
        }

        // Codigo postal is required for Mexico
        if (!formData.value.codigo_postal.trim()) {
            errors.value.codigo_postal = $t(
                "facturacion.validaciones.codigo-postal-requerido"
            );
            isValid = false;
        }

        // Regimen fiscal is required for Mexico
        if (!formData.value.regimen_fiscal_id) {
            errors.value.regimen_fiscal_id = $t(
                "facturacion.validaciones.regimen-requerido"
            );
            isValid = false;
        }

        // Uso CFDI is required for Mexico
        if (!formData.value.uso_cfdi_id) {
            errors.value.uso_cfdi_id = $t(
                "facturacion.validaciones.uso-cfdi-requerido"
            );
            isValid = false;
        }

        // Constancia file is required for Mexico
        const hasFile = Array.isArray(formData.value.constancia)
            ? formData.value.constancia.length > 0
            : formData.value.constancia !== null &&
              formData.value.constancia !== undefined;

        if (!hasFile) {
            errors.value.constancia = $t(
                "facturacion.validaciones.constancia-requerido"
            );
            isValid = false;
        }
    }

    return isValid;
};

// Determine route based on context
const getRouteInfo = () => {
    const hasOrderContext = props.forma && props.orden;

    if (hasOrderContext) {
        // Determine if it's forma-dinamica-a or forma-dinamica-b
        const formaUrl = props.forma?.url_formulario || "";
        const isFormaDinamicaA = formaUrl.includes("forma-dinamica-a");

        const formaParametros =
            props.forma?.parametros || props.forma?.clave || "default";

        if (isFormaDinamicaA) {
            return {
                routeName: "forma-dinamica-a.dato-fiscal-store",
                routeParams: {
                    stand: props.standId,
                    forma: formaParametros,
                },
            };
        } else {
            // forma-dinamica-b
            return {
                routeName: "forma-dinamica-b.dato-fiscal-store",
                routeParams: {
                    stand: props.standId,
                    forma: formaParametros,
                    orden: props.orden.id,
                },
            };
        }
    } else {
        // No order context - use datos-fiscales.store (for Profile.vue)
        return {
            routeName: "datos-fiscales.store",
            routeParams: props.standId,
        };
    }
};

// Handle save
const handleSave = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;

    try {
        // Create FormData for file upload
        const formDataToSend = new FormData();
        // Asegurarse de que el tipo sea un número
        formDataToSend.append("tipo", parseInt(formData.value.tipo, 10));
        formDataToSend.append("razon_social", formData.value.razon_social);

        if (formData.value.tipo === 1) {
            // Mexico specific fields
            formDataToSend.append("rfc", formData.value.rfc);
            formDataToSend.append(
                "codigo_postal",
                formData.value.codigo_postal
            );
            formDataToSend.append(
                "regimen_fiscal_id",
                formData.value.regimen_fiscal_id
            );
            formDataToSend.append("uso_cfdi_id", formData.value.uso_cfdi_id);

            // Extract file - can be array or single file
            const file = Array.isArray(formData.value.constancia)
                ? formData.value.constancia[0]
                : formData.value.constancia;

            if (file) {
                formDataToSend.append("constancia", file);
            }
        }

        // Add orden_id if available (for ServiceUtilitiesTab context)
        if (props.orden?.id) {
            formDataToSend.append("orden_id", props.orden.id);
        }

        // Add flag to request JSON response (for dialog mode)
        formDataToSend.append("return_json", "1");

        // Get route information based on context
        const routeInfo = getRouteInfo();

        // Check if we're in Profile context (no orden/forma)
        const isProfileContext = !props.forma || !props.orden;

        if (isProfileContext) {
            // For Profile context, use axios directly since the endpoint returns JSON
            // This allows us to handle the response and trigger the refresh properly
            try {
                const response = await axios.post(
                    route(routeInfo.routeName, routeInfo.routeParams),
                    formDataToSend,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                // Get the created dato fiscal from response
                const nuevoDatoFiscal = response.data?.dato_fiscal;

                // Reset form
                resetForm();

                // Emit saved event with the dato fiscal object
                // This will trigger the refresh in ProfileTab.vue via @saved="emit('refresh')"
                if (nuevoDatoFiscal) {
                    emit("saved", nuevoDatoFiscal);
                }

                // Close dialog and stop loading
                loading.value = false;
                show.value = false;
            } catch (error) {
                console.error("Error saving invoice data:", error);

                // Handle backend validation errors
                if (error.response?.data?.errors) {
                    const backendErrors = error.response.data.errors;
                    Object.keys(backendErrors).forEach((key) => {
                        if (errors.value.hasOwnProperty(key)) {
                            errors.value[key] = Array.isArray(
                                backendErrors[key]
                            )
                                ? backendErrors[key][0]
                                : backendErrors[key];
                        }
                    });
                }
                loading.value = false;
            }
        } else {
            // Order context - use axios directly to get the created dato fiscal
            // This allows us to get the dato fiscal in the response
            try {
                const response = await axios.post(
                    route(routeInfo.routeName, routeInfo.routeParams),
                    formDataToSend,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                            "X-Requested-With": "XMLHttpRequest", // Indicate AJAX request
                            Accept: "application/json", // Request JSON response
                        },
                    }
                );

                // Get the created dato fiscal from response
                const nuevoDatoFiscal = response.data?.dato_fiscal;

                // Reset form
                resetForm();

                // Emit saved event with the dato fiscal object
                if (nuevoDatoFiscal) {
                    emit("saved", nuevoDatoFiscal);
                } else {
                    // Fallback: fetch from list if not in response
                    try {
                        const datosFiscalesResponse = await axios.get(
                            route("datos-fiscales.list", {
                                stand: props.standId,
                            })
                        );
                        // Get the most recent one (should be the one we just created)
                        const latestDatoFiscal =
                            datosFiscalesResponse.data[
                                datosFiscalesResponse.data.length - 1
                            ];
                        if (latestDatoFiscal) {
                            emit("saved", latestDatoFiscal);
                        }
                    } catch (fetchError) {
                        console.error(
                            "Error fetching datos fiscales list:",
                            fetchError
                        );
                    }
                }

                // Close dialog and stop loading
                loading.value = false;
                show.value = false;
            } catch (error) {
                console.error("Error saving invoice data:", error);

                // Handle backend validation errors
                if (error.response?.data?.errors) {
                    const backendErrors = error.response.data.errors;
                    Object.keys(backendErrors).forEach((key) => {
                        if (errors.value.hasOwnProperty(key)) {
                            errors.value[key] = Array.isArray(
                                backendErrors[key]
                            )
                                ? backendErrors[key][0]
                                : backendErrors[key];
                        }
                    });
                }
                loading.value = false;
            }
        }
    } catch (error) {
        console.error("Error saving invoice data:", error);
        loading.value = false;
    }
};

// Handle cancel
const handleCancel = () => {
    resetForm();
    emit("cancel");
    show.value = false;
};

// Watch tipo changes to clear form when switching
watch(
    () => formData.value.tipo,
    (newTipo, oldTipo) => {
        if (oldTipo !== null && newTipo !== oldTipo) {
            // Clear form data except tipo
            formData.value.razon_social = "";
            formData.value.rfc = "";
            formData.value.codigo_postal = "";
            formData.value.regimen_fiscal_id = null;
            formData.value.uso_cfdi_id = null;
            formData.value.constancia = null;

            // Clear errors
            errors.value = {
                razon_social: "",
                rfc: "",
                codigo_postal: "",
                regimen_fiscal_id: "",
                uso_cfdi_id: "",
                constancia: "",
            };
        }
    }
);
</script>
